import * as React from "react";
import { cn } from "../lib/utils";

export type ProgressTone = "primary" | "success" | "warning" | "destructive" | "accent";

const BAR_TONE: Record<ProgressTone, string> = {
  primary: "bg-primary",
  success: "bg-success",
  warning: "bg-warning",
  destructive: "bg-destructive",
  accent: "bg-accent",
};

const TEXT_TONE: Record<ProgressTone, string> = {
  primary: "text-primary",
  success: "text-success",
  warning: "text-warning",
  destructive: "text-destructive",
  accent: "text-accent",
};

export interface ProgressBarProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "role"> {
  /** 0–100. Omit (or pass null) for an indeterminate bar. */
  value?: number | null;
  max?: number;
  tone?: ProgressTone;
  size?: "sm" | "md" | "lg";
  /** Accessible name — required unless visually labelled. */
  "aria-label"?: string;
  "aria-labelledby"?: string;
  /** Show the percentage on the right. */
  showValue?: boolean;
}

const ProgressBar = React.forwardRef<HTMLDivElement, ProgressBarProps>(
  (
    {
      className,
      value,
      max = 100,
      tone = "primary",
      size = "md",
      showValue = false,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      ...props
    },
    ref,
  ) => {
    const indeterminate = value == null;
    const pct = indeterminate ? 0 : Math.min(100, Math.max(0, (value! / max) * 100));
    return (
      <div ref={ref} className={cn("flex items-center gap-3", className)} {...props}>
        <div
          role="progressbar"
          aria-label={ariaLabel ?? "Progress"}
          aria-labelledby={ariaLabelledBy}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={indeterminate ? undefined : Math.round(pct)}
          aria-valuetext={indeterminate ? undefined : `${Math.round(pct)}%`}
          className={cn(
            "relative w-full overflow-hidden rounded-full bg-muted",
            size === "sm" && "h-1",
            size === "md" && "h-1.5",
            size === "lg" && "h-2.5",
          )}
        >
          <div
            aria-hidden="true"
            className={cn(
              "h-full rounded-full transition-[width] motion-safe:duration-300",
              BAR_TONE[tone],
              indeterminate && "w-1/3 motion-safe:animate-[asrt-marquee_1.4s_ease-in-out_infinite]",
            )}
            style={indeterminate ? undefined : { width: `${pct}%` }}
          />
        </div>
        {showValue && !indeterminate ? (
          <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">
            {Math.round(pct)}%
          </span>
        ) : null}
      </div>
    );
  },
);
ProgressBar.displayName = "ProgressBar";

export interface MeterProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "role"> {
  value: number;
  max?: number;
  label?: React.ReactNode;
  /** Show value/max text under the bar. */
  formattedValue?: React.ReactNode;
  /** Thresholds (fraction of max) that flip tone: below low → success, below high → warning, else destructive. */
  low?: number;
  high?: number;
  "aria-label"?: string;
}

/** Resource-usage meter (role="meter") — auto-tones against thresholds. */
const Meter = React.forwardRef<HTMLDivElement, MeterProps>(
  ({ className, value, max = 100, label, formattedValue, low = 0.6, high = 0.85, "aria-label": ariaLabel, ...props }, ref) => {
    const ratio = max > 0 ? Math.min(1, Math.max(0, value / max)) : 0;
    const tone: ProgressTone = ratio >= high ? "destructive" : ratio >= low ? "warning" : "success";
    return (
      <div ref={ref} className={cn("flex w-full flex-col gap-1.5", className)} {...props}>
        {(label || formattedValue) && (
          <div className="flex items-baseline justify-between text-sm">
            {label ? <span className="font-medium">{label}</span> : null}
            <span className={cn("font-mono text-xs tabular-nums", TEXT_TONE[tone])}>
              {formattedValue ?? `${value} / ${max}`}
            </span>
          </div>
        )}
        <div
          role="meter"
          aria-label={ariaLabel ?? (typeof label === "string" ? label : "Usage")}
          aria-valuemin={0}
          aria-valuemax={max}
          aria-valuenow={value}
          aria-valuetext={`${value} of ${max} (${Math.round(ratio * 100)}%)`}
          className="relative h-1.5 w-full overflow-hidden rounded-full bg-muted"
        >
          <div
            aria-hidden="true"
            className={cn("h-full rounded-full transition-[width] motion-safe:duration-300", BAR_TONE[tone])}
            style={{ width: `${ratio * 100}%` }}
          />
        </div>
      </div>
    );
  },
);
Meter.displayName = "Meter";

export { ProgressBar, Meter };
